import { useEffect, useRef, useState } from 'react';
import { CircleNotch, Lock, Check, Warning } from '@phosphor-icons/react';

import { getPlatformApiErrorMessage } from '@/lib/platformApi';

/**
 * The building blocks Platform Console settings sections are made of.
 *
 * These deliberately MIRROR the chambers Settings primitives
 * (`features/settings/sections/primitives.tsx`) rather than import them, for two reasons that
 * are not stylistic:
 *
 *  1. COLOUR. The console paints from `--plat-*` tokens because its sidebar is fixed dark in
 *     both themes; the chambers primitives paint from the tenant tokens. Importing them would
 *     put tenant colours on a console page, which `platform-theme-contrast.test.ts` exists to
 *     prevent.
 *  2. AUTHORITY. A chambers `FieldRow` renders from a server-resolved settings manifest
 *     (`canWrite` per setting per person). The Platform Console has no such registry and no
 *     role tiers — what is editable here is a fixed, small fact about the `PlatformAdmin`
 *     model, so `readOnly` is a prop rather than a lie about a manifest that does not exist.
 *
 * The rule they share is the one that matters: A FIELD NOBODY HERE MAY CHANGE RENDERS NO WRITE
 * CONTROL — a plain value and a line naming who does control it, never a disabled input. A
 * disabled box still invites the attempt.
 */

export function PlatformPanel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-plat-border bg-plat-surface">
      <header className="border-b border-plat-border px-5 py-4">
        <h3 className="text-sm font-semibold text-plat-fg">{title}</h3>
        {description && <p className="mt-0.5 text-xs text-plat-muted">{description}</p>}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

export function PlatformSectionIntro({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-5">
      <h2 className="text-lg font-semibold tracking-tight text-plat-fg">{title}</h2>
      <p className="mt-0.5 text-sm text-plat-muted">{description}</p>
    </div>
  );
}

/**
 * One row: a label, and either an editable control or the value as it stands.
 *
 * `readOnlyNote` is REQUIRED alongside `readOnly` by convention rather than by the type system,
 * because a value shown without it reads like something the person simply has not found the
 * edit button for. Every read-only row on this screen names its controller.
 */
export function PlatformFieldRow({
  label,
  hint,
  readOnly,
  readOnlyValue,
  readOnlyNote,
  children,
}: {
  label: string;
  hint?: string;
  /** True when this field is not the account holder's to change. */
  readOnly?: boolean;
  /** What the value is, for a field that is only read. */
  readOnlyValue?: React.ReactNode;
  /** Who does control it, and why it is not here. Shown under the value. */
  readOnlyNote?: React.ReactNode;
  /** The editable control. Rendered only when the field is writable. */
  children?: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5 border-b border-plat-border py-3.5 last:border-0 sm:grid-cols-[minmax(0,13rem)_1fr] sm:gap-4">
      <div className="pt-1.5">
        <label className="text-sm font-medium text-plat-fg">{label}</label>
        {hint && <p className="mt-0.5 text-xs text-plat-muted">{hint}</p>}
      </div>
      <div className="min-w-0">
        {readOnly ? (
          <div className="pt-1.5">
            <div className="text-sm text-plat-fg">
              {readOnlyValue ?? <span className="text-plat-muted">—</span>}
            </div>
            {readOnlyNote && (
              <p className="mt-1 flex items-start gap-1.5 text-xs text-plat-muted">
                <Lock className="mt-0.5 h-3 w-3 shrink-0" />
                <span>{readOnlyNote}</span>
              </p>
            )}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export function PlatformTextInput({
  value,
  onChange,
  placeholder,
  invalid,
  ...rest
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <input
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-invalid={invalid || undefined}
      className={`h-9 w-full rounded-lg border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint
        focus:outline-none focus:ring-2 focus:ring-plat-focus/50
        ${invalid ? 'border-plat-danger-line' : 'border-plat-border focus:border-plat-focus'}`}
    />
  );
}

export type PlatformSaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * What happened to the thing the operator just changed.
 *
 * Beside the control rather than as a toast: a toast that has faded cannot answer "did that
 * save?", and this screen is exactly where that question gets asked.
 */
export function PlatformSaveIndicator({ state, error }: { state: PlatformSaveState; error?: string | null }) {
  if (state === 'idle') return null;
  if (state === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-plat-muted">
        <CircleNotch className="h-3 w-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-plat-success-text">
        <Check className="h-3 w-3" weight="bold" /> Saved
      </span>
    );
  }
  return (
    <span className="inline-flex items-start gap-1.5 text-xs text-plat-danger-text">
      <Warning className="mt-0.5 h-3 w-3 shrink-0" weight="fill" />
      <span>{error ?? 'Could not save — your change has not been kept.'}</span>
    </span>
  );
}

/** Drives a PlatformSaveIndicator: saving → saved → back to idle, or error, which stays put. */
export function usePlatformSaveState() {
  const [state, setState] = useState<PlatformSaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  async function run(fn: () => Promise<unknown>): Promise<boolean> {
    clearTimeout(timer.current);
    setState('saving');
    setError(null);
    try {
      await fn();
      setState('saved');
      // An error stays on screen until the next attempt; a success fades, because a permanent
      // green tick beside every field is noise.
      timer.current = setTimeout(() => setState('idle'), 2500);
      return true;
    } catch (err) {
      setState('error');
      // The server's own words where it gave them — its validation message names the rule that
      // was broken, which "something went wrong" never does.
      setError(getPlatformApiErrorMessage(err, 'Could not save — your change has not been kept.'));
      return false;
    }
  }

  return { state, error, run };
}
